import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock, Calculator, CheckCircle, FloppyDisk, LockSimple,
  UploadSimple, DownloadSimple, FileArrowUp,
} from "@phosphor-icons/react";
import api, { API, formatApiError } from "@/lib/api";
import { RM, MONTHS } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Attendance() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("");
  const [run, setRun] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const download = (path, filename) => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const runImport = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file"); return; }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/attendance/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Attendance imported — ${data.updated} updated`);
      await loadRun(runId);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  useEffect(() => {
    api.get("/payroll/runs").then(({ data }) => {
      setRuns(data);
      const open = data.find((r) => ["draft", "review"].includes(r.status)) || data[0];
      if (open) setRunId(open.id);
    });
  }, []);

  const loadRun = (id) => api.get(`/payroll/runs/${id}`).then(({ data }) => {
    setRun(data);
    const d = {};
    data.items.forEach((it) => {
      d[it.id] = {
        overtime_hours: it.overtime_hours || 0,
        unpaid_leave_days: it.unpaid_leave_days || 0,
        bonus: it.bonus || 0,
        other_deductions: it.other_deductions || 0,
      };
    });
    setDraft(d);
  });

  useEffect(() => { if (runId) loadRun(runId); }, [runId]);

  const calculate = async () => {
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${runId}/calculate`);
      toast.success("Payroll calculated — you can now enter attendance");
      await loadRun(runId);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setField = (id, k, v) => setDraft((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));

  const saveRow = async (it) => {
    setSavingId(it.id);
    try {
      const f = draft[it.id];
      await api.put(`/payroll/items/${it.id}`, {
        overtime_hours: parseFloat(f.overtime_hours) || 0,
        unpaid_leave_days: parseFloat(f.unpaid_leave_days) || 0,
        bonus: parseFloat(f.bonus) || 0,
        other_deductions: parseFloat(f.other_deductions) || 0,
      });
      toast.success(`${it.employee_name} updated`);
      await loadRun(runId);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const locked = run && ["approved", "disbursed"].includes(run.status);
  const hasItems = run && run.items.length > 0;
  const cellCls = "h-9 w-20 border-[#E2E5EA] bg-[#F4F5F7] text-right text-[#0F172A] focus-visible:ring-red-500";

  return (
    <Layout
      title="Attendance & Overtime"
      subtitle="Capture overtime and unpaid leave that feed into payroll"
      actions={
        hasItems ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="att-template-btn" onClick={() => download("/payroll/attendance/template", "attendance_template.csv")} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-500">
              <DownloadSimple size={16} className="mr-1.5" /> Template
            </Button>
            <Button variant="outline" data-testid="att-export-btn" onClick={() => download(`/payroll/runs/${runId}/attendance/export`, "attendance.xlsx")} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-emerald-400">
              <DownloadSimple size={16} className="mr-1.5" /> Export
            </Button>
            {!locked && (
              <Button data-testid="att-import-btn" onClick={() => { setImportOpen(true); setResult(null); setFile(null); }} className="bg-red-500 font-semibold text-white hover:bg-red-600">
                <UploadSimple size={16} className="mr-1.5" /> Import
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="w-full max-w-xs">
          <Select value={runId} onValueChange={setRunId}>
            <SelectTrigger data-testid="attendance-run-select" className="border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]"><SelectValue placeholder="Select a payroll run" /></SelectTrigger>
            <SelectContent className="border-[#E2E5EA] bg-[#EEF1F5] text-[#0F172A]">
              {runs.map((r) => <SelectItem key={r.id} value={r.id}>{MONTHS[r.month]} {r.year}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {run && <StatusBadge status={run.status} />}
      </div>

      {runs.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-[#E2E5EA] bg-[#FFFFFF] py-16 text-center">
          <Clock size={36} className="text-[#C7CDD6]" />
          <p className="mt-3 font-heading text-lg font-semibold text-[#0F172A]">No payroll runs yet</p>
          <p className="mt-1 max-w-sm text-sm text-[#9CA3AF]">Create a payroll run first, then record attendance for it.</p>
          <Button onClick={() => navigate("/payroll")} className="mt-5 bg-red-500 font-semibold text-white hover:bg-red-600">Go to Payroll</Button>
        </div>
      ) : run && run.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-[#E2E5EA] bg-[#FFFFFF] py-16 text-center">
          <Calculator size={36} className="text-[#C7CDD6]" />
          <p className="mt-3 font-heading text-lg font-semibold text-[#0F172A]">Run not calculated yet</p>
          <p className="mt-1 max-w-sm text-sm text-[#9CA3AF]">Calculate the run to load employees, then enter their overtime and unpaid leave.</p>
          <Button onClick={calculate} disabled={busy} className="mt-5 bg-red-500 font-semibold text-white hover:bg-red-600">
            <Calculator size={16} className="mr-1.5" /> Calculate Payroll
          </Button>
        </div>
      ) : run ? (
        <>
          {locked && (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
              <LockSimple size={16} weight="fill" /> This run is {run.status} and locked — attendance can no longer be edited.
            </div>
          )}
          <div className="overflow-x-auto border border-[#E2E5EA] bg-[#FFFFFF]">
            <table className="w-full text-sm" data-testid="attendance-table">
              <thead>
                <tr className="border-b border-[#E2E5EA] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-3 py-3 text-right font-medium">OT Hours</th>
                  <th className="px-3 py-3 text-right font-medium">Unpaid Days</th>
                  <th className="px-3 py-3 text-right font-medium">Bonus (RM)</th>
                  <th className="px-3 py-3 text-right font-medium">Other Ded. (RM)</th>
                  <th className="px-4 py-3 text-right font-medium">Net Pay</th>
                  <th className="px-4 py-3 text-right font-medium">Save</th>
                </tr>
              </thead>
              <tbody>
                {run.items.map((it) => {
                  const f = draft[it.id] || {};
                  return (
                    <tr key={it.id} data-testid={`att-row-${it.employee_no}`} className="border-b border-[#EDEFF2] hover:bg-[#EEF1F5]">
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-[#0F172A]">{it.employee_name}</p>
                        <p className="text-xs text-[#6B7280]">{it.employee_no} · {it.department}</p>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Input data-testid={`att-ot-${it.employee_no}`} type="number" disabled={locked} value={f.overtime_hours} onChange={(e) => setField(it.id, "overtime_hours", e.target.value)} className={cellCls} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Input data-testid={`att-unpaid-${it.employee_no}`} type="number" disabled={locked} value={f.unpaid_leave_days} onChange={(e) => setField(it.id, "unpaid_leave_days", e.target.value)} className={cellCls} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Input type="number" disabled={locked} value={f.bonus} onChange={(e) => setField(it.id, "bonus", e.target.value)} className={cellCls} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Input type="number" disabled={locked} value={f.other_deductions} onChange={(e) => setField(it.id, "other_deductions", e.target.value)} className={cellCls} />
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-red-500">{RM(it.net_pay)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          data-testid={`att-save-${it.employee_no}`}
                          disabled={locked || savingId === it.id}
                          onClick={() => saveRow(it)}
                          className="rounded-md border border-[#E2E5EA] p-2 text-[#9CA3AF] transition-colors duration-200 hover:border-emerald-500/40 hover:text-emerald-400 disabled:opacity-40"
                          title="Save & recalculate"
                        >
                          {savingId === it.id ? <CheckCircle size={16} weight="fill" className="text-emerald-400" /> : <FloppyDisk size={16} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="h-40 animate-pulse rounded-md bg-[#FFFFFF]" />
      )}

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Import Attendance</DialogTitle>
            <DialogDescription className="text-[#9CA3AF]">
              Upload a CSV or Excel with columns: employee_no, overtime_hours, unpaid_leave_days, bonus, other_deductions. Matched by employee number.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => download("/payroll/attendance/template", "attendance_template.csv")} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-500">
                <DownloadSimple size={16} className="mr-1.5" /> CSV Template
              </Button>
              <Button variant="outline" onClick={() => download(`/payroll/runs/${runId}/attendance/export`, "attendance.xlsx")} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-emerald-400">
                <DownloadSimple size={16} className="mr-1.5" /> Export Current
              </Button>
            </div>
            <div onClick={() => fileRef.current?.click()} className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-[#E2E5EA] bg-[#F4F5F7] py-8 transition-colors duration-200 hover:border-red-500/40">
              <FileArrowUp size={32} className="text-[#C7CDD6]" />
              <p className="mt-2 text-sm text-[#0F172A]">{file ? file.name : "Click to choose a CSV or Excel file"}</p>
              <p className="text-xs text-[#6B7280]">.csv or .xlsx</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" data-testid="att-import-file" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
            </div>
            {result && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-[#9CA3AF]">
                <span className="text-emerald-400">{result.updated} updated</span>
                {result.not_found_count > 0 && <> · <span className="text-red-500">{result.not_found_count} not found</span></>}
                {result.error_count > 0 && <> · <span className="text-red-400">{result.error_count} errors</span></>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">Close</Button>
            <Button data-testid="att-run-import" onClick={runImport} disabled={importing || !file} className="bg-red-500 font-semibold text-white hover:bg-red-600">
              <UploadSimple size={16} className="mr-1.5" /> {importing ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
